import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const SeniorsPage = () => {
  const { t } = useTranslation('common');
  const [selectedCommunity, setSelectedCommunity] = useState<any>(null);
  const navigate = useNavigate();

  const communities = [
    {
      id: 'family',
      icon: 'ri-group-line',
      title: t('seniors_community_family'),
      description: t('seniors_community_family_desc'),
      color: 'green',
      members: t('seniors_community_family_members'),
      fullDescription: t('seniors_community_family_full'),
      topics: [
        t('seniors_community_family_topic1'),
        t('seniors_community_family_topic2'),
        t('seniors_community_family_topic3'),
        t('seniors_community_family_topic4'),
        t('seniors_community_family_topic5')
      ]
    },
    {
      id: 'stories',
      icon: 'ri-book-read-line',
      title: t('seniors_community_stories'),
      description: t('seniors_community_stories_desc'),
      color: 'teal',
      members: t('seniors_community_stories_members'),
      fullDescription: t('seniors_community_stories_full'),
      topics: [
        t('seniors_community_stories_topic1'),
        t('seniors_community_stories_topic2'),
        t('seniors_community_stories_topic3'),
        t('seniors_community_stories_topic4'),
        t('seniors_community_stories_topic5')
      ]
    },
    {
      id: 'friends',
      icon: 'ri-chat-smile-line',
      title: t('seniors_community_friends'),
      description: t('seniors_community_friends_desc'),
      color: 'emerald',
      members: t('seniors_community_friends_members'),
      fullDescription: t('seniors_community_friends_full'),
      topics: [
        t('seniors_community_friends_topic1'),
        t('seniors_community_friends_topic2'),
        t('seniors_community_friends_topic3'),
        t('seniors_community_friends_topic4'),
        t('seniors_community_friends_topic5')
      ]
    },
    {
      id: 'events',
      icon: 'ri-calendar-event-line',
      title: t('seniors_community_events'),
      description: t('seniors_community_events_desc'),
      color: 'lime',
      members: t('seniors_community_events_members'),
      fullDescription: t('seniors_community_events_full'),
      topics: [
        t('seniors_community_events_topic1'),
        t('seniors_community_events_topic2'),
        t('seniors_community_events_topic3'),
        t('seniors_community_events_topic4'),
        t('seniors_community_events_topic5')
      ]
    },
    {
      id: 'learning',
      icon: 'ri-lightbulb-line',
      title: t('seniors_community_learning'),
      description: t('seniors_community_learning_desc'),
      color: 'green',
      members: t('seniors_community_learning_members'),
      fullDescription: t('seniors_community_learning_full'),
      topics: [
        t('seniors_community_learning_topic1'),
        t('seniors_community_learning_topic2'),
        t('seniors_community_learning_topic3'),
        t('seniors_community_learning_topic4'),
        t('seniors_community_learning_topic5')
      ]
    },
    {
      id: 'support',
      icon: 'ri-hand-heart-line',
      title: t('seniors_community_support'),
      description: t('seniors_community_support_desc'),
      color: 'teal',
      members: t('seniors_community_support_members'),
      fullDescription: t('seniors_community_support_full'),
      topics: [
        t('seniors_community_support_topic1'),
        t('seniors_community_support_topic2'),
        t('seniors_community_support_topic3'),
        t('seniors_community_support_topic4'),
        t('seniors_community_support_topic5')
      ]
    }
  ];

  const getColorClasses = (color: string) => {
    const colors: any = {
      green: {
        bg: 'from-green-900/30 to-green-900/20',
        icon: 'bg-green-500',
        border: 'hover:border-green-400'
      },
      teal: {
        bg: 'from-teal-900/30 to-teal-900/20',
        icon: 'bg-teal-500',
        border: 'hover:border-teal-400'
      },
      emerald: {
        bg: 'from-emerald-900/30 to-emerald-900/20',
        icon: 'bg-emerald-500',
        border: 'hover:border-emerald-400'
      },
      lime: {
        bg: 'from-lime-900/30 to-lime-900/20',
        icon: 'bg-lime-500',
        border: 'hover:border-lime-400'
      },
      orange: {
        bg: 'from-orange-900/30 to-orange-900/20',
        icon: 'bg-orange-500',
        border: 'hover:border-orange-400'
      },
      yellow: {
        bg: 'from-yellow-900/30 to-yellow-900/20',
        icon: 'bg-yellow-500',
        border: 'hover:border-yellow-400'
      },
      red: {
        bg: 'from-red-900/30 to-red-900/20',
        icon: 'bg-red-500',
        border: 'hover:border-red-400'
      }
    };
    return colors[color];
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    element?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleCommunityClick = (communityId: string) => {
    const community = communities.find(c => c.id === communityId);
    if (community) {
      setSelectedCommunity(community);
    }
  };

  const handleCardClick = (path: string) => {
    navigate(path);
  };

  return (
    <div className="min-h-screen bg-black font-['Inter']">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 transition-all duration-300 backdrop-blur-md bg-black/90 border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <img 
                src="https://static.readdy.ai/image/a79563fe53efb1b307a7324e5db6bc2e/126dd8573ffd64afbaee10d6ee258da0.jpeg"
                alt="FaceTrue Logo" 
                className="w-10 h-10 rounded-full"
              />
              <span className="text-xl font-bold text-white">FaceTrue Seniors</span>
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('features')} className="text-gray-400 hover:text-green-400 transition-colors text-sm font-medium cursor-pointer">
                {t('seniors_nav_features')}
              </button>
              <button onClick={() => scrollToSection('health')} className="text-gray-400 hover:text-green-400 transition-colors text-sm font-medium cursor-pointer">
                {t('seniors_nav_health')}
              </button>
              <button onClick={() => scrollToSection('community')} className="text-gray-400 hover:text-green-400 transition-colors text-sm font-medium cursor-pointer">
                {t('seniors_nav_community')}
              </button>
              <a 
                href="/video-seniors"
                className="bg-gradient-to-r from-green-500 to-teal-500 text-black px-5 py-2 rounded-full font-bold hover:scale-105 transition-all whitespace-nowrap cursor-pointer no-underline flex items-center space-x-2 shadow-lg"
              >
                <i className="ri-video-line"></i>
                <span>🎬 VIDEO</span>
              </a>
            </div>
            <a 
              href="/"
              className="bg-green-500 text-black px-6 py-2 rounded-full font-semibold hover:bg-green-400 transition-all whitespace-nowrap cursor-pointer"
            >
              {t('seniors_back_home')}
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="min-h-screen flex items-center justify-center relative overflow-hidden pt-20">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/12652cd878d77c1bf9e619c6c1ad305c.jpg"
            alt="Seniors Background"
            className="w-full h-full object-cover object-top opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/70 to-black"></div>
        </div>
        
        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
          <div className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center mb-8 mx-auto shadow-2xl">
            <i className="ri-user-heart-line text-5xl text-black"></i>
          </div>
          
          <h1 className="text-5xl md:text-7xl font-black mb-8 leading-tight text-white">
            {t('seniors_hero_title')}<br />
            <span className="bg-gradient-to-r from-green-400 to-teal-400 bg-clip-text text-transparent">
              {t('seniors_hero_subtitle')}
            </span>
          </h1>
          
          <p className="text-xl md:text-2xl text-gray-300 mb-12 leading-relaxed max-w-4xl mx-auto" dangerouslySetInnerHTML={{ __html: t('seniors_hero_desc') }} />
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">{t('seniors_features_title')}</h2>
            <p className="text-xl text-gray-400">{t('seniors_features_subtitle')}</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Family Connections */}
            <button
              onClick={() => handleCardClick('/')}
              className="bg-gray-900 border border-gray-800 rounded-3xl p-8 hover:border-green-400 hover:shadow-xl hover:shadow-green-500/10 transition-all duration-300 text-left w-full cursor-pointer"
            >
              <div className="w-16 h-16 bg-green-900/30 rounded-full flex items-center justify-center mb-6">
                <i className="ri-parent-line text-3xl text-green-400"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4 text-white">{t('seniors_feature1_title')}</h3>
              <p className="text-gray-400 leading-relaxed">
                {t('seniors_feature1_desc')}
              </p>
            </button>

            {/* Share Your Story */}
            <button
              onClick={() => handleCardClick('/')}
              className="bg-gray-900 border border-gray-800 rounded-3xl p-8 hover:border-cyan-400 hover:shadow-xl hover:shadow-cyan-500/10 transition-all duration-300 text-left w-full cursor-pointer"
            >
              <div className="w-16 h-16 bg-cyan-900/30 rounded-full flex items-center justify-center mb-6">
                <i className="ri-book-open-line text-3xl text-cyan-400"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4 text-white">{t('seniors_feature2_title')}</h3>
              <p className="text-gray-400 leading-relaxed">
                {t('seniors_feature2_desc')}
              </p>
            </button>

            {/* Make New Friends */}
            <button
              onClick={() => handleCardClick('/')}
              className="bg-gray-900 border border-gray-800 rounded-3xl p-8 hover:border-fuchsia-400 hover:shadow-xl hover:shadow-fuchsia-500/10 transition-all duration-300 text-left w-full cursor-pointer"
            >
              <div className="w-16 h-16 bg-fuchsia-900/30 rounded-full flex items-center justify-center mb-6">
                <i className="ri-user-add-line text-3xl text-fuchsia-400"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4 text-white">{t('seniors_feature3_title')}</h3>
              <p className="text-gray-400 leading-relaxed">
                {t('seniors_feature3_desc')}
              </p>
            </button>

            {/* Local Events */}
            <button
              onClick={() => handleCardClick('/')}
              className="bg-gray-900 border border-gray-800 rounded-3xl p-8 hover:border-orange-400 hover:shadow-xl hover:shadow-orange-500/10 transition-all duration-300 text-left w-full cursor-pointer"
            >
              <div className="w-16 h-16 bg-orange-900/30 rounded-full flex items-center justify-center mb-6">
                <i className="ri-calendar-event-line text-3xl text-orange-400"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4 text-white">{t('seniors_feature4_title')}</h3>
              <p className="text-gray-400 leading-relaxed">
                {t('seniors_feature4_desc')}
              </p>
            </button>

            {/* Learn New Skills */}
            <button
              onClick={() => handleCardClick('/')}
              className="bg-gray-900 border border-gray-800 rounded-3xl p-8 hover:border-pink-400 hover:shadow-xl hover:shadow-pink-500/10 transition-all duration-300 text-left w-full cursor-pointer"
            >
              <div className="w-16 h-16 bg-pink-900/30 rounded-full flex items-center justify-center mb-6">
                <i className="ri-lightbulb-line text-3xl text-pink-400"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4 text-white">{t('seniors_feature5_title')}</h3>
              <p className="text-gray-400 leading-relaxed">
                {t('seniors_feature5_desc')}
              </p>
            </button>

            {/* Support Groups */}
            <button
              onClick={() => handleCardClick('/')}
              className="bg-gray-900 border border-gray-800 rounded-3xl p-8 hover:border-teal-400 hover:shadow-xl hover:shadow-teal-500/10 transition-all duration-300 text-left w-full cursor-pointer"
            >
              <div className="w-16 h-16 bg-teal-900/30 rounded-full flex items-center justify-center mb-6">
                <i className="ri-heart-pulse-line text-3xl text-teal-400"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4 text-white">{t('seniors_feature6_title')}</h3>
              <p className="text-gray-400 leading-relaxed">
                {t('seniors_feature6_desc')}
              </p>
            </button>
          </div>
        </div>
      </section>

      {/* Health Section */}
      <section id="health" className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">{t('seniors_health_title')}</h2>
            <p className="text-xl text-gray-400">
              {t('seniors_health_subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gray-900 border border-gray-800 rounded-3xl p-8">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-heart-pulse-line text-black text-xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2 text-white">{t('seniors_health1_title')}</h3>
                  <p className="text-gray-400">{t('seniors_health1_desc')}</p>
                </div>
              </div>
            </div>

            <div className="bg-gray-900 border border-gray-800 rounded-3xl p-8">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-mental-health-line text-black text-xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2 text-white">{t('seniors_health2_title')}</h3>
                  <p className="text-gray-400">{t('seniors_health2_desc')}</p>
                </div>
              </div>
            </div>

            <div className="bg-gray-900 border border-gray-800 rounded-3xl p-8">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-medicine-bottle-line text-black text-xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2 text-white">{t('seniors_health3_title')}</h3>
                  <p className="text-gray-400">{t('seniors_health3_desc')}</p>
                </div>
              </div>
            </div>

            <div className="bg-gray-900 border border-gray-800 rounded-3xl p-8">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-lime-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-walk-line text-black text-xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2 text-white">{t('seniors_health4_title')}</h3>
                  <p className="text-gray-400">{t('seniors_health4_desc')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Community Section */}
      <section id="community" className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">{t('seniors_community_title')}</h2>
            <p className="text-xl text-gray-400">
              {t('seniors_community_subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <a 
              href="#family"
              onClick={(e) => {
                e.preventDefault();
                handleCommunityClick('family');
              }}
              className="bg-gray-900 border border-gray-800 rounded-2xl p-6 hover:border-green-400 hover:shadow-xl hover:shadow-green-500/10 transition-all cursor-pointer active:scale-95 block no-underline"
            >
              <div className="w-14 h-14 bg-green-900/30 rounded-full flex items-center justify-center mb-4">
                <i className="ri-group-line text-2xl text-green-400"></i>
              </div>
              <h3 className="font-bold text-white mb-2">{t('seniors_community_family')}</h3>
              <p className="text-sm text-gray-400">{t('seniors_community_family_desc')}</p>
            </a>

            <a 
              href="#stories"
              onClick={(e) => {
                e.preventDefault();
                handleCommunityClick('stories');
              }}
              className="bg-gray-900 border border-gray-800 rounded-2xl p-6 hover:border-teal-400 hover:shadow-xl hover:shadow-teal-500/10 transition-all cursor-pointer active:scale-95 block no-underline"
            >
              <div className="w-14 h-14 bg-teal-900/30 rounded-full flex items-center justify-center mb-4">
                <i className="ri-book-read-line text-2xl text-teal-400"></i>
              </div>
              <h3 className="font-bold text-white mb-2">{t('seniors_community_stories')}</h3>
              <p className="text-sm text-gray-400">{t('seniors_community_stories_desc')}</p>
            </a>

            <a 
              href="#friends"
              onClick={(e) => {
                e.preventDefault();
                handleCommunityClick('friends');
              }}
              className="bg-gray-900 border border-gray-800 rounded-2xl p-6 hover:border-emerald-400 hover:shadow-xl hover:shadow-emerald-500/10 transition-all cursor-pointer active:scale-95 block no-underline"
            >
              <div className="w-14 h-14 bg-emerald-900/30 rounded-full flex items-center justify-center mb-4">
                <i className="ri-chat-smile-line text-2xl text-emerald-400"></i>
              </div>
              <h3 className="font-bold text-white mb-2">{t('seniors_community_friends')}</h3>
              <p className="text-sm text-gray-400">{t('seniors_community_friends_desc')}</p>
            </a>

            <a 
              href="#events"
              onClick={(e) => {
                e.preventDefault();
                handleCommunityClick('events');
              }}
              className="bg-gray-900 border border-gray-800 rounded-2xl p-6 hover:border-lime-400 hover:shadow-xl hover:shadow-lime-500/10 transition-all cursor-pointer active:scale-95 block no-underline"
            >
              <div className="w-14 h-14 bg-lime-900/30 rounded-full flex items-center justify-center mb-4">
                <i className="ri-calendar-event-line text-2xl text-lime-400"></i>
              </div>
              <h3 className="font-bold text-white mb-2">{t('seniors_community_events')}</h3>
              <p className="text-sm text-gray-400">{t('seniors_community_events_desc')}</p>
            </a>

            <a 
              href="#learning"
              onClick={(e) => {
                e.preventDefault();
                handleCommunityClick('learning');
              }}
              className="bg-gray-900 border border-gray-800 rounded-2xl p-6 hover:border-green-400 hover:shadow-xl hover:shadow-green-500/10 transition-all cursor-pointer active:scale-95 block no-underline"
            >
              <div className="w-14 h-14 bg-green-900/30 rounded-full flex items-center justify-center mb-4">
                <i className="ri-lightbulb-line text-2xl text-green-400"></i>
              </div>
              <h3 className="font-bold text-white mb-2">{t('seniors_community_learning')}</h3>
              <p className="text-sm text-gray-400">{t('seniors_community_learning_desc')}</p>
            </a>

            <a 
              href="#support"
              onClick={(e) => {
                e.preventDefault();
                handleCommunityClick('support');
              }}
              className="bg-gray-900 border border-gray-800 rounded-2xl p-6 hover:border-teal-400 hover:shadow-xl hover:shadow-teal-500/10 transition-all cursor-pointer active:scale-95 block no-underline"
            >
              <div className="w-14 h-14 bg-teal-900/30 rounded-full flex items-center justify-center mb-4">
                <i className="ri-hand-heart-line text-2xl text-teal-400"></i>
              </div>
              <h3 className="font-bold text-white mb-2">{t('seniors_community_support')}</h3>
              <p className="text-sm text-gray-400">{t('seniors_community_support_desc')}</p>
            </a>
          </div>
        </div>
      </section>

      {/* Community Detail Popup */}
      {selectedCommunity && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedCommunity(null)}
        >
          <div 
            className="bg-gray-900 border border-gray-800 rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-8">
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center space-x-4">
                  <div className={`w-16 h-16 ${getColorClasses(selectedCommunity.color).icon} rounded-full flex items-center justify-center`}>
                    <i className={`${selectedCommunity.icon} text-2xl text-black`}></i>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white">{selectedCommunity.title}</h3>
                    <p className="text-gray-400">{selectedCommunity.members}</p>
                  </div>
                </div>
                <button 
                  onClick={() => setSelectedCommunity(null)}
                  className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-800 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-2xl text-gray-400"></i>
                </button>
              </div>

              <p className="text-gray-300 text-lg mb-6 leading-relaxed">
                {selectedCommunity.fullDescription}
              </p>

              <div className="mb-6">
                <h4 className="font-bold text-white mb-3">{t('seniors_community_popular_topics')}</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedCommunity.topics.map((topic: string, index: number) => (
                    <span 
                      key={index}
                      className="px-4 py-2 bg-gray-800 text-gray-300 rounded-full text-sm font-medium"
                    >
                      {topic}
                    </span>
                  ))}
                </div>
              </div>

              <button 
                onClick={() => setSelectedCommunity(null)}
                className="w-full bg-gradient-to-r from-green-500 to-teal-500 text-black py-4 rounded-xl font-bold text-lg hover:scale-105 transition-all shadow-xl whitespace-nowrap cursor-pointer"
              >
                {t('seniors_community_join_btn')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Benefits Section */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">{t('seniors_benefits_title')}</h2>
            <p className="text-xl text-gray-400">
              {t('seniors_benefits_subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 hover:shadow-xl hover:shadow-green-500/10 transition-all">
              <div className="flex items-center space-x-3 mb-3">
                <i className="ri-check-line text-green-400 text-2xl"></i>
                <h3 className="font-bold text-white">{t('seniors_benefit1_title')}</h3>
              </div>
              <p className="text-gray-400 text-sm">{t('seniors_benefit1_desc')}</p>
            </div>

            <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 hover:shadow-xl hover:shadow-green-500/10 transition-all">
              <div className="flex items-center space-x-3 mb-3">
                <i className="ri-check-line text-green-400 text-2xl"></i>
                <h3 className="font-bold text-white">{t('seniors_benefit2_title')}</h3>
              </div>
              <p className="text-gray-400 text-sm">{t('seniors_benefit2_desc')}</p>
            </div>

            <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 hover:shadow-xl hover:shadow-green-500/10 transition-all">
              <div className="flex items-center space-x-3 mb-3">
                <i className="ri-check-line text-green-400 text-2xl"></i>
                <h3 className="font-bold text-white">{t('seniors_benefit3_title')}</h3>
              </div>
              <p className="text-gray-400 text-sm">{t('seniors_benefit3_desc')}</p>
            </div>

            <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 hover:shadow-xl hover:shadow-green-500/10 transition-all">
              <div className="flex items-center space-x-3 mb-3">
                <i className="ri-check-line text-green-400 text-2xl"></i>
                <h3 className="font-bold text-white">{t('seniors_benefit4_title')}</h3>
              </div>
              <p className="text-gray-400 text-sm">{t('seniors_benefit4_desc')}</p>
            </div>

            <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 hover:shadow-xl hover:shadow-green-500/10 transition-all">
              <div className="flex items-center space-x-3 mb-3">
                <i className="ri-check-line text-green-400 text-2xl"></i>
                <h3 className="font-bold text-white">{t('seniors_benefit5_title')}</h3>
              </div>
              <p className="text-gray-400 text-sm">{t('seniors_benefit5_desc')}</p>
            </div>

            <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 hover:shadow-xl hover:shadow-green-500/10 transition-all">
              <div className="flex items-center space-x-3 mb-3">
                <i className="ri-check-line text-green-400 text-2xl"></i>
                <h3 className="font-bold text-white">{t('seniors_benefit6_title')}</h3>
              </div>
              <p className="text-gray-400 text-sm">{t('seniors_benefit6_desc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* News Feed Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">
            <i className="ri-newspaper-line mr-3"></i>
            Community Feed
          </h2>

          {/* Post Creation */}
          <div className="bg-white rounded-2xl p-6 mb-6 border-2 border-green-500 shadow-lg">
            <div className="flex items-center space-x-4 mb-4">
              <img 
                src="https://readdy.ai/api/search-image?query=friendly-senior-elderly-person-avatar-portrait-with-warm-smile-and-kind-expression-wearing-comfortable-clothing-in-bright-home-setting-with-clean-simple-background-high-quality-headshot&width=100&height=100&seq=seniors-avatar-1&orientation=squarish"
                alt="User"
                className="w-12 h-12 rounded-full object-cover border-2 border-green-500"
              />
              <input
                type="text"
                placeholder="What's on your mind?"
                className="flex-1 bg-gray-100 rounded-full px-6 py-3 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div className="flex items-center justify-between pt-4 border-t border-gray-200">
              <div className="flex space-x-4">
                <button className="flex items-center space-x-2 px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-image-add-line text-xl text-green-600"></i>
                  <span className="text-gray-700 font-medium">Photo/Video</span>
                </button>
                <button className="flex items-center space-x-2 px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-emotion-happy-line text-xl text-orange-600"></i>
                  <span className="text-gray-700 font-medium">Feeling</span>
                </button>
              </div>
              <button className="bg-gradient-to-r from-green-600 to-green-500 text-white px-6 py-2 rounded-lg font-bold hover:scale-105 transition-all shadow-md cursor-pointer whitespace-nowrap">
                Post
              </button>
            </div>
          </div>

          {/* News Feed Posts */}
          <div className="space-y-6">
            {/* Post 1 */}
            <div className="bg-white rounded-2xl p-6 border-2 border-orange-500 shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <img 
                    src="https://readdy.ai/api/search-image?query=friendly-senior-elderly-woman-avatar-portrait-with-warm-smile-wearing-comfortable-clothing-in-bright-home-setting-with-clean-simple-background-high-quality-headshot-photography&width=100&height=100&seq=seniors-user-1&orientation=squarish"
                    alt="User"
                    className="w-12 h-12 rounded-full object-cover border-2 border-orange-500"
                  />
                  <div>
                    <h3 className="font-bold text-gray-900">Mrs. Nguyen Thi Lan</h3>
                    <p className="text-sm text-gray-600">2 hours ago</p>
                  </div>
                </div>
                <button className="text-gray-500 hover:text-gray-700 cursor-pointer">
                  <i className="ri-more-fill text-2xl"></i>
                </button>
              </div>
              <p className="text-gray-800 mb-4 leading-relaxed">
                This morning I went to exercise with friends at the park. Fresh air, refreshing spirit! 🌳☀️
              </p>
              <img 
                src="https://readdy.ai/api/search-image?query=group-of-happy-senior-elderly-people-exercising-together-in-bright-sunny-park-with-green-trees-doing-morning-tai-chi-or-light-exercise-with-clean-simple-background-healthy-active-lifestyle&width=800&height=500&seq=seniors-post-1&orientation=landscape"
                alt="Post"
                className="w-full h-80 object-cover rounded-xl mb-4"
              />
              <div className="flex items-center justify-between py-3 border-t border-b border-gray-200 mb-3">
                <span className="text-gray-700 font-medium">❤️ 156 likes</span>
                <span className="bg-gradient-to-r from-pink-500 via-purple-500 to-cyan-500 bg-clip-text text-transparent font-bold">RESPECT 18</span>
                <span className="text-gray-700 font-medium">34 comments</span>
              </div>
              <div className="flex items-center justify-around">
                <button className="flex items-center space-x-2 px-4 py-2 rounded-lg hover:bg-pink-50 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-heart-line text-xl text-pink-600"></i>
                  <span className="text-gray-900 font-semibold">Like</span>
                </button>
                <button className="flex items-center space-x-2 px-4 py-2 rounded-lg hover:bg-blue-50 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-chat-3-line text-xl text-blue-600"></i>
                  <span className="text-gray-900 font-semibold">Comment</span>
                </button>
                <button className="flex items-center space-x-2 px-4 py-2 rounded-lg hover:bg-green-50 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-share-forward-line text-xl text-green-600"></i>
                  <span className="text-gray-900 font-semibold">Share</span>
                </button>
              </div>
            </div>

            {/* Post 2 */}
            <div className="bg-white rounded-2xl p-6 border-2 border-orange-500 shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <img 
                    src="https://readdy.ai/api/search-image?query=friendly-senior-elderly-man-avatar-portrait-with-warm-smile-wearing-comfortable-casual-clothing-in-bright-home-setting-with-clean-simple-background-high-quality-headshot-photography&width=100&height=100&seq=seniors-user-2&orientation=squarish"
                    alt="User"
                    className="w-12 h-12 rounded-full object-cover border-2 border-orange-500"
                  />
                  <div>
                    <h3 className="font-bold text-gray-900">Mr. Tran Van Minh</h3>
                    <p className="text-sm text-gray-600">4 hours ago</p>
                  </div>
                </div>
                <button className="text-gray-500 hover:text-gray-700 cursor-pointer">
                  <i className="ri-more-fill text-2xl"></i>
                </button>
              </div>
              <p className="text-gray-800 mb-4 leading-relaxed">
                Today my grandchild came to visit, playing with them was so much fun! Family is the most precious thing. 👨‍👩‍👧‍👦❤️
              </p>
              <div className="flex items-center justify-between py-3 border-t border-b border-gray-200 mb-3">
                <span className="text-gray-700 font-medium">❤️ 203 likes</span>
                <span className="bg-gradient-to-r from-pink-500 via-purple-500 to-cyan-500 bg-clip-text text-transparent font-bold">RESPECT 18</span>
                <span className="text-gray-700 font-medium">48 comments</span>
              </div>
              <div className="flex items-center justify-around">
                <button className="flex items-center space-x-2 px-4 py-2 rounded-lg hover:bg-pink-50 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-heart-line text-xl text-pink-600"></i>
                  <span className="text-gray-900 font-semibold">Like</span>
                </button>
                <button className="flex items-center space-x-2 px-4 py-2 rounded-lg hover:bg-blue-50 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-chat-3-line text-xl text-blue-600"></i>
                  <span className="text-gray-900 font-semibold">Comment</span>
                </button>
                <button className="flex items-center space-x-2 px-4 py-2 rounded-lg hover:bg-green-50 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-share-forward-line text-xl text-green-600"></i>
                  <span className="text-gray-900 font-semibold">Share</span>
                </button>
              </div>
            </div>

            {/* Post 3 */}
            <div className="bg-white rounded-2xl p-6 border-2 border-orange-500 shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <img 
                    src="https://readdy.ai/api/search-image?query=friendly-senior-elderly-woman-avatar-portrait-with-kind-smile-wearing-comfortable-clothing-in-bright-cozy-home-with-clean-simple-background-high-quality-headshot-photography&width=100&height=100&seq=seniors-user-3&orientation=squarish"
                    alt="User"
                    className="w-12 h-12 rounded-full object-cover border-2 border-orange-500"
                  />
                  <div>
                    <h3 className="font-bold text-gray-900">Mrs. Le Thi Hoa</h3>
                    <p className="text-sm text-gray-600">1 day ago</p>
                  </div>
                </div>
                <button className="text-gray-500 hover:text-gray-700 cursor-pointer">
                  <i className="ri-more-fill text-2xl"></i>
                </button>
              </div>
              <p className="text-gray-800 mb-4 leading-relaxed">
                My flower garden is blooming beautifully! Taking care of plants is my daily joy. 🌺🌸
              </p>
              <img 
                src="https://readdy.ai/api/search-image?query=beautiful-colorful-flower-garden-in-bright-sunny-day-with-blooming-roses-and-various-flowers-in-well-maintained-home-garden-with-clean-simple-background-peaceful-gardening-scene&width=800&height=500&seq=seniors-post-3&orientation=landscape"
                alt="Post"
                className="w-full h-80 object-cover rounded-xl mb-4"
              />
              <div className="flex items-center justify-between py-3 border-t border-b border-gray-200 mb-3">
                <span className="text-gray-700 font-medium">❤️ 278 likes</span>
                <span className="bg-gradient-to-r from-pink-500 via-purple-500 to-cyan-500 bg-clip-text text-transparent font-bold">RESPECT 18</span>
                <span className="text-gray-700 font-medium">62 comments</span>
              </div>
              <div className="flex items-center justify-around">
                <button className="flex items-center space-x-2 px-4 py-2 rounded-lg hover:bg-pink-50 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-heart-line text-xl text-pink-600"></i>
                  <span className="text-gray-900 font-semibold">Like</span>
                </button>
                <button className="flex items-center space-x-2 px-4 py-2 rounded-lg hover:bg-blue-50 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-chat-3-line text-xl text-blue-600"></i>
                  <span className="text-gray-900 font-semibold">Comment</span>
                </button>
                <button className="flex items-center space-x-2 px-4 py-2 rounded-lg hover:bg-green-50 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-share-forward-line text-xl text-green-600"></i>
                  <span className="text-gray-900 font-semibold">Share</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 bg-gray-900 border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <img 
            src="https://static.readdy.ai/image/a79563fe53efb1b307a7324e5db6bc2e/126dd8573ffd64afbaee10d6ee258da0.jpeg"
            alt="FaceTrue Logo" 
            className="w-8 h-8 mx-auto mb-4 rounded-full"
          />
          <p className="text-gray-400 text-sm mb-2">
            {t('seniors_footer_copyright')}
          </p>
          <p className="text-gray-500 text-sm mb-4">
            {t('seniors_footer_desc')}
          </p>
          <a 
            href="https://readdy.ai/?origin=logo" 
            className="text-gray-600 text-xs hover:text-gray-400 transition-colors"
            target="_blank"
            rel="noopener noreferrer"
          >
            Powered by Readdy
          </a>
        </div>
      </footer>
    </div>
  );
};

export default SeniorsPage;